import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { linkedinUrl } from "@/data/content";

const navItems = [
  { label: "Accueil", href: "/" },
  { label: "Services", href: "/services" },
  { label: "À propos", href: "/a-propos" },
  { label: "Équipe & Partenaires", href: "/equipe" },
  { label: "Actualités", href: "/actualites" },
  { label: "Contact", href: "/contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const dark = theme === "dark";

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-background/70 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" className="flex items-center" data-testid="brand-logo-link" aria-label="Accueil Intellix">
          <img src="/logo-intellix.webp" alt="Intellix Kaizzen" className="h-20 w-auto sm:h-24" />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-").replace("à", "a")}-link`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-muted-foreground"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Button variant="ghost" size="icon" onClick={() => setTheme(dark ? "light" : "dark")} data-testid="theme-toggle-button" aria-label="Changer de thème">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button asChild className="rounded-full px-6 font-semibold" data-testid="header-contact-button">
            <Link to="/contact">Parlons-en</Link>
          </Button>
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setTheme(dark ? "light" : "dark")} data-testid="mobile-theme-toggle-button" aria-label="Changer de thème">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Ouvrir le menu">
            {open ? <X /> : <Menu />}
          </Button>
        </div>
      </div>
      {open ? (
        <nav className="border-t border-border bg-background px-5 py-5 lg:hidden" data-testid="mobile-navigation" aria-label="Navigation mobile">
          <div className="flex flex-col gap-4">
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href} onClick={() => setOpen(false)} className="text-base font-medium" data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}-link`}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      ) : null}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border/70 bg-card/50" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-12 sm:px-8 lg:grid-cols-[1.3fr_0.7fr]">
        <div>
          <Link to="/" className="flex items-center" data-testid="footer-brand-link">
            <img src="/logo-intellix.webp" alt="Intellix Kaizzen" className="h-12 w-auto" />
          </Link>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Partenaire des PME pour une transformation numérique sécurisée, performante et durable.
          </p>
        </div>
        <div className="flex flex-col gap-3 text-sm text-muted-foreground lg:items-end">
          <a href={linkedinUrl} target="_blank" rel="noreferrer" className="transition-colors hover:text-primary" data-testid="footer-linkedin-link">
            LinkedIn Intellix
          </a>
          <Link to="/contact" className="transition-colors hover:text-primary" data-testid="footer-contact-link">
            Nous contacter
          </Link>
          <Link to="/mentions-legales" className="transition-colors hover:text-primary" data-testid="footer-mentions-legales-link">
            Mentions légales
          </Link>
          <Link to="/politique-de-confidentialite" className="transition-colors hover:text-primary" data-testid="footer-politique-confidentialite-link">
            Politique de confidentialité
          </Link>
          <span>© 2026 Intellix. Tous droits réservés.</span>
        </div>
      </div>
    </footer>
  );
}
